import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Truck, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  ClipboardCheck,
  Search,
  Trash2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BodegaSupplierOrders = ({ embedded = false }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Forms
  const [orderForm, setOrderForm] = useState({
    supplierName: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedDelivery: '',
    items: [],
    paymentTerms: 'cod',
    notes: ''
  });
  
  const [newItem, setNewItem] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unit: 'pcs',
    unitPrice: ''
  });
  
  const [receiveForm, setReceiveForm] = useState({
    receivedItems: [],
    notes: ''
  });
  
  const [rejectReason, setRejectReason] = useState('');

  // Access check
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  
  // Supplier Orders: Lhoren, Nicole, Arbe, Pabs, Kristine, Geraldine, Cristel
  const canCreateOrders = isAdmin || ['lhoren', 'nicole', 'arbe', 'pabs', 'kristine', 'geraldine', 'cristel'].some(n => username.includes(n) || fullName.includes(n));
  
  // Order Approval: Nicole, Arbe, Pabs, Kristine
  const canApproveOrders = isAdmin || ['nicole', 'arbe', 'pabs', 'kristine'].some(n => username.includes(n) || fullName.includes(n));
  
  // Receiving/Verification: Geraldine, Kristine, Cristel, Nicole
  const canReceiveOrders = isAdmin || ['geraldine', 'kristine', 'cristel', 'nicole'].some(n => username.includes(n) || fullName.includes(n));

  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/nas/bodega/supplier-orders`);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/nas/products`);
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval': return 'bg-amber-100 text-amber-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'received': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_approval': return 'Pending Approval';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'received': return 'Received';
      default: return status;
    }
  };

  // Add item to order
  const addItemToOrder = () => {
    if (!newItem.productName || !newItem.quantity || !newItem.unitPrice) {
      toast.error('Please fill in product name, quantity, and unit price');
      return;
    }
    
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, {
        ...newItem,
        quantity: parseFloat(newItem.quantity),
        unitPrice: parseFloat(newItem.unitPrice),
        totalPrice: parseFloat(newItem.quantity) * parseFloat(newItem.unitPrice)
      }]
    });
    
    setNewItem({ productId: '', productName: '', quantity: '', unit: 'pcs', unitPrice: '' });
  };

  const removeItemFromOrder = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };

  const calculateOrderTotal = () => {
    return orderForm.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  };

  // Create order
  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.supplierName || orderForm.items.length === 0) {
      toast.error('Please fill in supplier name and add at least one item');
      return;
    }

    try {
      await axios.post(`${API}/api/nas/bodega/supplier-orders`, {
        ...orderForm,
        totalAmount: calculateOrderTotal()
      });
      toast.success('Supplier order created successfully');
      setShowCreateDialog(false);
      setOrderForm({
        supplierName: '', orderDate: new Date().toISOString().split('T')[0],
        expectedDelivery: '', items: [], paymentTerms: 'cod', notes: ''
      });
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  // Approve order
  const handleApproveOrder = async (orderId) => {
    try {
      await axios.put(`${API}/api/nas/bodega/supplier-orders/${orderId}/approve`);
      toast.success('Order approved');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order');
    }
  };

  // Reject order
  const handleRejectOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/api/nas/bodega/supplier-orders/${selectedOrder.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Order rejected');
      setShowRejectDialog(false);
      setSelectedOrder(null);
      setRejectReason('');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject order');
    }
  };

  // Open receive dialog
  const openReceiveDialog = (order) => {
    setSelectedOrder(order);
    setReceiveForm({
      receivedItems: order.items.map(item => ({
        ...item,
        receivedQuantity: item.quantity,
        qualityOk: true
      })),
      notes: ''
    });
    setShowReceiveDialog(true);
  };

  // Receive order
  const handleReceiveOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/api/nas/bodega/supplier-orders/${selectedOrder.id}/receive`, null, {
        params: {
          notes: receiveForm.notes
        }
      });
      toast.success('Order received! Items added to Bodega inventory.');
      setShowReceiveDialog(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive order');
    }
  };

  // Filter orders by status
  const filteredOrders = orders.filter(order => {
    if (activeTab === 'all') return true;
    return order.status === activeTab;
  });

  // Count by status
  const counts = {
    all: orders.length,
    pending_approval: orders.filter(o => o.status === 'pending_approval').length,
    approved: orders.filter(o => o.status === 'approved').length,
    received: orders.filter(o => o.status === 'received').length,
    rejected: orders.filter(o => o.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"}>
      {/* Header */}
      {!embedded && (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Bodega Supplier Orders</h1>
            <p className="text-slate-600">Create and manage orders from suppliers to Bodega</p>
          </div>
          {canCreateOrders && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus size={18} className="mr-2" />
              Create Order
            </Button>
          )}
        </div>
      )}
      
      {/* Create button when embedded */}
      {embedded && canCreateOrders && (
        <div className="flex justify-end">
          <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus size={18} className="mr-2" />
            Create Bodega Order
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('all')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-800">{counts.all}</p>
            <p className="text-sm text-slate-500">All Orders</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-amber-50" onClick={() => setActiveTab('pending_approval')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{counts.pending_approval}</p>
            <p className="text-sm text-amber-700">Pending</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-blue-50" onClick={() => setActiveTab('approved')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{counts.approved}</p>
            <p className="text-sm text-blue-700">Approved</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-green-50" onClick={() => setActiveTab('received')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{counts.received}</p>
            <p className="text-sm text-green-700">Received</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md bg-red-50" onClick={() => setActiveTab('rejected')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{counts.rejected}</p>
            <p className="text-sm text-red-700">Rejected</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck size={20} />
            Supplier Orders
            <span className="text-sm font-normal text-slate-500">
              ({filteredOrders.length} {activeTab === 'all' ? '' : getStatusLabel(activeTab).toLowerCase()})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Order #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Supplier</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Created By</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredOrders.map(order => (
                  <tr key={order.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-sm font-medium">{order.orderNumber}</td>
                    <td className="px-4 py-3 font-medium">{order.supplierName}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(order.orderDate)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-slate-100 rounded-full text-xs">
                        {order.items?.length || 0} items
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold">{formatCurrency(order.totalAmount)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{order.createdBy}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-center">
                        {/* Approval Actions */}
                        {order.status === 'pending_approval' && canApproveOrders && (
                          <>
                            <Button 
                              size="sm" 
                              onClick={() => handleApproveOrder(order.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setSelectedOrder(order); setShowRejectDialog(true); }}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <XCircle size={14} className="mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        
                        {/* Receive Action */}
                        {order.status === 'approved' && canReceiveOrders && (
                          <Button 
                            size="sm" 
                            onClick={() => openReceiveDialog(order)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Package size={14} className="mr-1" />
                            Receive
                          </Button>
                        )}
                        
                        {/* View Details */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedOrder(order); }}
                        >
                          <FileText size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Supplier Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrder} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier Name *</Label>
                <Input
                  value={orderForm.supplierName}
                  onChange={e => setOrderForm({...orderForm, supplierName: e.target.value})}
                  placeholder="Enter supplier name"
                  required
                />
              </div>
              <div>
                <Label>Order Date *</Label>
                <Input
                  type="date"
                  value={orderForm.orderDate}
                  onChange={e => setOrderForm({...orderForm, orderDate: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={orderForm.expectedDelivery}
                  onChange={e => setOrderForm({...orderForm, expectedDelivery: e.target.value})}
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={orderForm.paymentTerms}
                  onValueChange={v => setOrderForm({...orderForm, paymentTerms: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cod">Cash on Delivery</SelectItem>
                    <SelectItem value="credit_7">7 Days Credit</SelectItem>
                    <SelectItem value="credit_15">15 Days Credit</SelectItem>
                    <SelectItem value="credit_30">30 Days Credit</SelectItem>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Items */}
            <div className="border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold">Order Items</h4>
              
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <Label>Product</Label>
                  <Select
                    value={newItem.productId}
                    onValueChange={v => {
                      const product = products.find(p => p.id === v);
                      setNewItem({
                        ...newItem,
                        productId: v,
                        productName: product?.name || '',
                        unitPrice: product?.purchasePrice || product?.sellingPricePerSack || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Qty</Label>
                  <Input
                    type="number"
                    value={newItem.quantity}
                    onChange={e => setNewItem({...newItem, quantity: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Unit</Label>
                  <Select
                    value={newItem.unit}
                    onValueChange={v => setNewItem({...newItem, unit: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">Pieces</SelectItem>
                      <SelectItem value="sack">Sacks</SelectItem>
                      <SelectItem value="kg">KG</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="bottle">Bottle</SelectItem>
                      <SelectItem value="pack">Pack</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    value={newItem.unitPrice}
                    onChange={e => setNewItem({...newItem, unitPrice: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-span-2">
                  <Button type="button" onClick={addItemToOrder} className="w-full">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>

              {/* Items List */}
              {orderForm.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Product</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-center">Unit</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {orderForm.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{item.productName}</td>
                          <td className="px-3 py-2 text-center">{item.quantity}</td>
                          <td className="px-3 py-2 text-center">{item.unit}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.totalPrice)}</td>
                          <td className="px-3 py-2 text-center">
                            <button 
                              type="button" 
                              onClick={() => removeItemFromOrder(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right font-semibold">Total:</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-600">
                          {formatCurrency(calculateOrderTotal())}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={e => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Additional notes"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Create Order
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receive Order Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Receive Order: {selectedOrder?.orderNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Supplier:</strong> {selectedOrder?.supplierName}
              </p>
              <p className="text-sm text-blue-700">
                <strong>Total Amount:</strong> {formatCurrency(selectedOrder?.totalAmount)}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Items to Receive</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-center">Ordered</th>
                      <th className="px-3 py-2 text-center">Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {receiveForm.receivedItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">{item.productName}</td>
                        <td className="px-3 py-2 text-center font-medium">{item.quantity}</td>
                        <td className="px-3 py-2 text-center">{item.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <Label>Receiving Notes</Label>
              <Textarea
                value={receiveForm.notes}
                onChange={e => setReceiveForm({...receiveForm, notes: e.target.value})}
                placeholder="Any issues or notes about the delivery"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReceiveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleReceiveOrder} className="bg-green-600 hover:bg-green-700">
                <CheckCircle size={16} className="mr-2" />
                Confirm Receipt
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Are you sure you want to reject order <strong>{selectedOrder?.orderNumber}</strong>?
            </p>
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleRejectOrder} className="bg-red-600 hover:bg-red-700">
                <XCircle size={16} className="mr-2" />
                Reject Order
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodegaSupplierOrders;
